import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Contact, platformApi } from '@/services/platformApi';
import ContactProfile from '@/components/contacts/ContactProfile';
import { LoadingSpinner } from '@vaiu/ui';
import { ErrorMessage } from '@vaiu/ui';

const ContactDetailPage: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [merchantProfile, setMerchantProfile] = useState<any>(null);
  
  // Cache for merchant profiles (similar to Contacts page)
  const merchantProfilesCacheRef = useRef<Map<string, any>>(new Map());
  const fetchingMerchantsRef = useRef(false);

  useEffect(() => {
    const loadContact = async () => {
      if (!contactId) {
        setError('Contact ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Use the improved API endpoint to fetch a single contact directly
        const foundContact = await platformApi.getContact(contactId, { includeTransactions: true });
        
        if (!foundContact) {
          setError('Contact not found');
          setLoading(false);
          return;
        }
        
        setContact(foundContact);
        
        // Fetch merchant profile if this is a business contact
        if (foundContact.relationship === 'business' && foundContact.merchantId) {
          await fetchMerchantProfile(foundContact.merchantId);
        }
        
      } catch (err: any) {
        console.error('Failed to load contact:', err);
        setError(err.message || 'Failed to load contact');
      } finally {
        setLoading(false);
      }
    };

    loadContact();
  }, [contactId]);

  const fetchMerchantProfile = async (merchantId: string) => {
    // Check cache first
    if (merchantProfilesCacheRef.current.has(merchantId)) {
      setMerchantProfile(merchantProfilesCacheRef.current.get(merchantId));
      return;
    }

    // Prevent multiple simultaneous calls
    if (fetchingMerchantsRef.current) {
      return;
    }

    fetchingMerchantsRef.current = true;

    try {
      const batchResult = await platformApi.merchant.getPublicMerchantProfilesBatch([merchantId]);
      
      if (batchResult.merchants && batchResult.merchants.length > 0) {
        const profile = batchResult.merchants[0];
        merchantProfilesCacheRef.current.set(merchantId, profile);
        setMerchantProfile(profile);
      }
    } catch (error) {
      console.error('Failed to fetch merchant profile:', error);
      // Don't show error to user, just continue without merchant profile
    } finally {
      fetchingMerchantsRef.current = false;
    }
  };

  const handleSendMoney = () => {
    if (!contact) return;
    const payeeId = contact.merchantId || contact.id;
    navigate(`/pay?payeeId=${payeeId}`);
  };

  const handleViewMessages = (contact: Contact, transactionId?: string) => {
    if (transactionId) {
      navigate(`/messages?transactionId=${transactionId}&contactId=${contact.id}`, {
        state: { contactName: contact.name }
      });
    } else {
      if (contact.transactions && contact.transactions.length > 0) {
        const recentTransaction = contact.transactions[0];
        const txId = typeof recentTransaction === 'string' 
          ? recentTransaction 
          : recentTransaction.id || recentTransaction._id || recentTransaction.key;
        
        if (txId) {
          navigate(`/messages?transactionId=${txId}&contactId=${contact.id}`, {
            state: { contactName: contact.name }
          });
        } else {
          navigate(`/messages?contactId=${contact.id}`, {
            state: { contactName: contact.name }
          });
        }
      } else {
        navigate(`/messages?contactId=${contact.id}`, {
          state: { contactName: contact.name }
        });
      }
    }
  };

  const handleBack = () => {
    navigate('/contacts');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Contacts
            </Button>
          </div>
          <ErrorMessage
            message={error || 'Contact not found'}
            onRetry={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <div className="mb-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Contacts
          </Button>
        </div>

        {/* Contact Profile Content */}
        <ContactProfile
          contact={contact}
          onSendMoney={handleSendMoney}
          onViewMessages={handleViewMessages}
          merchantProfile={merchantProfile}
          onBack={handleBack}
        />
      </div>
    </div>
  );
};

export default ContactDetailPage;
